import * as z from 'zod';

export const BulkInviteRequest = z.object({
  policyIds: z.array(z.string()),
  options: z
    .object({
      emails: z.array(z.string()).min(1).max(10),
      invitePurpose: z
        .string()
        .describe('Details explaining the purpose of the invite.')
        .optional()
        .nullable(),
      defaultEmailLanguage: z
        .string()
        .describe(
          "Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language."
        )
        .optional()
        .nullable(),
      locationRestriction: z
        .object({ locationIds: z.array(z.string()).max(20).optional() })
        .describe('Location restriction for the invite.')
        .optional(),
    })
    .describe('Filter options. The `emails` field **must** be passed.'),
});
export const BulkInviteResponse = z.object({
  invites: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Invite ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        siteId: z
          .string()
          .describe('Site ID the user is invited to as a collaborator.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        email: z
          .string()
          .describe('Email address where the invite was sent.')
          .email()
          .optional(),
        policyIds: z.array(z.string()).optional(),
        inviterId: z
          .string()
          .describe('Deprecated. Use `inviterAccountId`.')
          .optional(),
        status: z
          .enum(['Pending', 'Used', 'Deleted', 'Declined', 'Expired'])
          .describe(
            "Invite Status.\n\nSupported values:\n- **Pending:** The invite has been sent and is valid, waiting for the user's response.\n- **Used:** The invite has been accepted.\n- **Deleted:** The invite has been deleted or revoked.\n- **Declined:** The user declined the invite.\n- **Expired:** The invite has expired without being accepted."
          )
          .optional(),
        acceptLink: z
          .string()
          .describe('Link to accept the invite.')
          .optional(),
        inviterAccountId: z
          .string()
          .describe('Inviting account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        acceptedByAccountId: z
          .string()
          .describe(
            'Account ID that accepted the invite. Populated only once the invite is accepted.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        dateCreated: z
          .date()
          .describe('Date the invite was created.')
          .optional()
          .nullable(),
        staffId: z
          .string()
          .describe("User's Wix Bookings staff ID, if relevant.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        expirationDate: z
          .date()
          .describe('Invite expiration date')
          .optional()
          .nullable(),
        locationIds: z.array(z.string()).max(20).optional(),
        callerCapabilities: z.array(z.string()).max(20).optional(),
      })
    )
    .optional(),
  failedEmails: z.array(z.string()).optional(),
});
export const ResendInviteRequest = z.object({
  inviteId: z.string().describe('Invite ID.'),
  options: z
    .object({
      defaultEmailLanguage: z
        .string()
        .describe(
          "Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language."
        )
        .optional()
        .nullable(),
    })
    .describe('Filter options.')
    .optional(),
});
export const ResendInviteResponse = z.object({
  invite: z
    .object({
      _id: z
        .string()
        .describe('Invite ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      siteId: z
        .string()
        .describe('Site ID the user is invited to as a collaborator.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      email: z
        .string()
        .describe('Email address where the invite was sent.')
        .email()
        .optional(),
      policyIds: z.array(z.string()).optional(),
      inviterId: z
        .string()
        .describe('Deprecated. Use `inviterAccountId`.')
        .optional(),
      status: z
        .enum(['Pending', 'Used', 'Deleted', 'Declined', 'Expired'])
        .describe(
          "Invite Status.\n\nSupported values:\n- **Pending:** The invite has been sent and is valid, waiting for the user's response.\n- **Used:** The invite has been accepted.\n- **Deleted:** The invite has been deleted or revoked.\n- **Declined:** The user declined the invite.\n- **Expired:** The invite has expired without being accepted."
        )
        .optional(),
      acceptLink: z.string().describe('Link to accept the invite.').optional(),
      inviterAccountId: z
        .string()
        .describe('Inviting account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      acceptedByAccountId: z
        .string()
        .describe(
          'Account ID that accepted the invite. Populated only once the invite is accepted.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      dateCreated: z
        .date()
        .describe('Date the invite was created.')
        .optional()
        .nullable(),
      staffId: z
        .string()
        .describe("User's Wix Bookings staff ID, if relevant.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      expirationDate: z
        .date()
        .describe('Invite expiration date')
        .optional()
        .nullable(),
      locationIds: z.array(z.string()).max(20).optional(),
      callerCapabilities: z.array(z.string()).max(20).optional(),
    })
    .describe('Invites that were sent.')
    .optional(),
});
export const RevokeInviteRequest = z.object({
  inviteId: z.string().describe('Invite ID.'),
});
export const RevokeInviteResponse = z.object({});
